<template>
	<div class="chart-wrap" ref="chart"></div>
</template>

<script>
	import * as echarts from 'echarts';
	import apis from "@/http/apis";
	import chartClickMixin from "@/views/index/components/handlemixin.js";

	// 资产类型对应单位映射
	const UNIT_MAP = {
		'门市':      '处',
		'办公室':    '间',
		'房产':      '套',
		'土地及附着物': '宗·个',
		'电影院':    '处',
		'土地':      '宗',
		'住宅':      '套',
		'酒店':      '栋',
		'公寓':      '处',
		'地上附着物': '个',
	};

	// 每根柱子的渐变色: [顶色, 底色]
	const BAR_GRADIENTS = [
		['#07e1ff', '#0050cc'],
		['#ffd600', '#ff6a00'],
		['#13c81c', '#006630'],
		['#ff7eb3', '#c2185b'],
		['#a78bfa', '#5b21b6'],
		['#34d399', '#065f46'],
		['#f97316', '#92400e'],
		['#60a5fa', '#1e3a8a'],
		['#52c8ff', '#0369a1'],
		['#fb923c', '#7c2d12'],
	];

	export default {
		mixins: [chartClickMixin],
		name: 'BarFenlei',
		data() {
			return {
				chart: null,
				cardData: [],
				chartName: '存量资产分类统计',
				chartType: 'bar'
			};
		},
		created() {
			this.fetchData();
		},
		mounted() {
			this.$nextTick(() => {
				this.chart = echarts.init(this.$refs.chart);
				this.renderChart();
				window.addEventListener('resize', this.handleResize);
			});
		},
		beforeDestroy() {
			if (this.chart) this.chart.dispose();
			window.removeEventListener('resize', this.handleResize);
		},
		methods: {
			fetchData() {
				apis.stockAssetCategory()
					.then((res) => {
						const list = (res && res.data && res.data.list) ? res.data.list : [];
						const sorted = list
							.map(item => ({
								title:    item.type,
								typeCode: item.typeCode,
								count:    item.count != null ? Number(item.count) : 0,
								unit:     UNIT_MAP[item.type] || item.unit || '处',
							}))
							.sort((a, b) => b.count - a.count);

						const total = sorted.reduce((s, i) => s + i.count, 0) || 1;
						this.cardData = sorted.map((item, idx) => ({
							...item,
							rank: idx + 1,
							pct:  total > 0 ? Math.round((item.count / total) * 1000) / 10 : 0,
							gradient: BAR_GRADIENTS[idx % BAR_GRADIENTS.length],
						}));
						this.renderChart();
					})
					.catch((err) => {
						console.error('存量资产分类接口请求失败', err);
					});
			},

			renderChart() {
				if (!this.chart) return;

				const data = this.cardData;
				if (!data.length) return;

				const maxCount = Math.max(...data.map(d => d.count), 1);
				// y轴最大值留出顶部标签空间（约30%余量）
				const yMax = Math.ceil(maxCount * 1.45);

				// 背景柱（slot效果，让图表看起来更"满"）
				const bgData = data.map(() => yMax);

				const barData = data.map((d, idx) => ({
					value: d.count,
					itemStyle: {
						color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
							{ offset: 0,   color: d.gradient[0] },
							{ offset: 0.6, color: d.gradient[0] + 'bb' },
							{ offset: 1,   color: d.gradient[1] + '66' },
						]),
						borderRadius: [4, 4, 0, 0],
						shadowColor: d.gradient[0] + '88',
						shadowBlur: 8,
						shadowOffsetY: -2,
					}
				}));

				const option = {
					grid: {
						top: '22%',
						bottom: '14%',
						left: '2%',
						right: '2%',
						containLabel: true,
					},
					xAxis: {
						type: 'category',
						data: data.map(d => d.title),
						axisLine: {
							lineStyle: { color: 'rgba(7,225,255,0.25)' }
						},
						axisTick: { show: false },
						axisLabel: {
							color: '#c8e6ff',
							fontSize: 12,
							interval: 0,
							formatter: (val) => val.length > 4 ? val.slice(0, 3) + '\n' + val.slice(3) : val,
						},
					},
					yAxis: {
						type: 'value',
						min: 0,
						max: yMax,
						axisLine: { show: false },
						axisTick: { show: false },
						axisLabel: { show: false },
						splitLine: {
							lineStyle: { color: 'rgba(7,225,255,0.07)', type: 'dashed' }
						},
					},
					series: [
						// 背景槽柱
						{
							type: 'bar',
							data: bgData,
							barMaxWidth: 36,
							barCategoryGap: '25%',
							silent: true,
							itemStyle: {
								color: 'rgba(7,225,255,0.06)',
								borderRadius: [4, 4, 0, 0],
								borderColor: 'rgba(7,225,255,0.12)',
								borderWidth: 1,
							},
							z: 1,
						},
						// 数据柱
						{
							type: 'bar',
							data: barData,
							barMaxWidth: 36,
							barCategoryGap: '25%',
							barGap: '-100%',
							z: 2,
							label: {
								show: true,
								position: 'top',
								distance: 6,
								formatter: (params) => {
									const d = data[params.dataIndex];
									return `{count|${d.count}${d.unit}}\n{pct|${d.pct}%}`;
								},
								rich: {
									count: {
										color: '#ffffff',
										fontSize: 13,
										fontWeight: 'bold',
										lineHeight: 18,
										textShadowColor: 'rgba(7,225,255,0.6)',
										textShadowBlur: 4,
									},
									pct: {
										color: '#fde047',
										fontSize: 11,
										fontWeight: '600',
										lineHeight: 16,
									},
								},
							},
						},
					],
				};

				this.chart.setOption(option, true);

				// 绑定点击事件
				this.chart.off('click');
				this.chart.on('click', (params) => {
					if (params.seriesIndex === 0) return; // 忽略背景柱点击
					const item = data[params.dataIndex];
					if (item) this.setupChartClick(item, 'shuju');
				});
			},

			handleResize() {
				if (this.chart) this.chart.resize();
			},

			// 覆盖 mixin 的空方法
			initChartWithClick() {},
		}
	};
</script>

<style scoped>
	.chart-wrap {
		width: 100%;
		height: 100%;
	}
</style>